import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckroomIcon from '@mui/icons-material/Checkroom';

import { OperationService } from '../../../services';
import type { OperationResult, SourceImageRef } from '../../../domain';
import {
  DEFAULT_TRY_ON_STATE,
  TRY_ON_GARMENT_CATEGORIES,
  buildTryOnRequest,
  hasSourceImage,
  type TryOnFormState,
  type TryOnGarmentCategory,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';
import { ReferenceImageInput } from '../references/ReferenceImageInput';
import { mergePromptWithSwatches } from '../../shared/panels/swatchSelection';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  /** The canvas's active image becomes the model image for try-on. */
  source: SourceImageRef | undefined;
  swatchPrompt?: string;
  onResult?: (result: OperationResult) => void;
}

export const TryOnPanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  swatchPrompt,
  onResult,
}) => {
  const [state, setState] = useState<TryOnFormState>(DEFAULT_TRY_ON_STATE);
  const [garmentRef, setGarmentRef] = useState<SourceImageRef | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // TryOnRequest does NOT extend SourceImageRef — the canvas image lives
  // under `modelImage{Id|Url|Base64}` (the garment lives under
  // `garmentImage*`). Tell useOperationMutation to retarget the bake-on-
  // submit at the model triplet so a user's filter sliders bake into the
  // model image, not nowhere.
  const mutation = useOperationMutation(OperationService.tryOn, {
    canvasSourceField: 'modelImage',
  });

  const hasModel = hasSourceImage(source);
  const hasGarment = !!garmentRef;

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A model image (the active canvas image) is required.');
      return;
    }
    try {
      const sid = await ensureSession();
      // Swatches merge into the panel's "additional prompt" field —
      // garment fabric / color hints are exactly what additionalPrompt
      // is for.
      const mergedAdditional = mergePromptWithSwatches(state.additionalPrompt, swatchPrompt ?? '');
      const request = buildTryOnRequest(
        { ...state, additionalPrompt: mergedAdditional },
        sid,
        source,
        garmentRef,
      );
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled =
    mutation.isPending || isCreatingSession || !hasModel || !hasGarment;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <CheckroomIcon fontSize="small" />
        <Typography variant="subtitle2">Try on</Typography>
      </Stack>

      {!hasModel && (
        <Alert severity="info">
          The active canvas image is the model. Generate or select one first.
        </Alert>
      )}

      <ReferenceImageInput
        label="Garment"
        helperText="The clothing item to composite onto the model"
        value={garmentRef}
        onChange={setGarmentRef}
      />

      <FormControl size="small" fullWidth>
        <InputLabel id="tryon-category-label">Garment category</InputLabel>
        <Select
          labelId="tryon-category-label"
          label="Garment category"
          value={state.garmentCategory}
          onChange={e =>
            setState(s => ({ ...s, garmentCategory: e.target.value as TryOnGarmentCategory }))
          }
        >
          {TRY_ON_GARMENT_CATEGORIES.map(opt => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Additional prompt (optional)"
        value={state.additionalPrompt}
        onChange={e => setState(s => ({ ...s, additionalPrompt: e.target.value }))}
        placeholder="e.g., natural studio lighting"
        size="small"
        fullWidth
      />

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Try-on failed.'}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <CheckroomIcon />
            )
          }
        >
          {isCreatingSession ? 'Starting session…' : mutation.isPending ? 'Compositing…' : 'Try on'}
        </Button>
      </Box>
    </Stack>
  );
};
